import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ForecastResult, SimulationMetadata } from '@/types/forecast';
import { StatsCard } from '@/components/ui';

interface ForecastChartProps {
  results: ForecastResult[];
  isLoading: boolean;
  title: string;
  className?: string;
  includeConfidenceIntervals?: boolean;
  includePlannedFailures?: boolean;
  granularityMonths?: number;
}

interface ForecastStatsProps {
  results: ForecastResult[];
  isLoading: boolean;
  confidenceLevel?: number;
  populationSize?: number;
  forecastSubsetSize?: number;
  simulationMetadata?: SimulationMetadata | null;
}

export const ForecastStats: React.FC<ForecastStatsProps> = ({
  results,
  isLoading: _isLoading,
  confidenceLevel = 0.90,
  populationSize = 0,
  forecastSubsetSize = 0,
  simulationMetadata = null
}) => {
  const summaryStats = useMemo(() => {
    if (!results.length) return null;
    
    // Get the last result for total cumulative values
    const lastResult = results[results.length - 1];
    const totalExpectedFailures = lastResult?.cumulativeFailures || 0;
    
    // Calculate total planned failures
    const totalPlannedFailures = results.reduce((sum, r) => sum + (r.plannedFailures || 0), 0);
    
    // Check if we have confidence intervals
    const hasCumulativeCI = lastResult?.cumulativeLowerBound !== undefined && 
                           lastResult?.cumulativeUpperBound !== undefined;
    
    // Use granularity from the actual results
    const granularityMonths = results[0]?.granularityMonths || 12;
    const periodType = granularityMonths === 12 ? 'Year' : 
                      granularityMonths === 3 ? 'Quarter' : 'Month';
    const periodTypeLower = periodType.toLowerCase();
    const periodTypeAdjective = granularityMonths === 12 ? 'Annual' : 
                               granularityMonths === 3 ? 'Quarterly' : 'Monthly';
    const periodsPerYear = 12 / granularityMonths;
    
    // Calculate average per period
    const averagePerPeriod = totalExpectedFailures / results.length;
    
    // Find peak period (including planned failures if present)
    const peakPeriodTotal = Math.max(...results.map(r => r.expectedFailures + (r.plannedFailures || 0)));
    const peakPeriodIndex = results.findIndex(r => (r.expectedFailures + (r.plannedFailures || 0)) === peakPeriodTotal);
    const peakPeriod = peakPeriodIndex >= 0 ? results[peakPeriodIndex]?.timePeriod || '—' : '—';
    
    // Check for period confidence intervals
    const peakPeriodResult = results[peakPeriodIndex];
    const hasPeriodCI = peakPeriodResult?.lowerBound !== undefined && 
                        peakPeriodResult?.upperBound !== undefined;
    
    const populationSizeFromResults = results[0]?.populationSize || 0;
    
    // Determine subset status
    let subsetDescription = 'Turbines included in forecast';
    if (populationSize > 0 && forecastSubsetSize > 0) {
      const percentage = Math.round((forecastSubsetSize / populationSize) * 100);
      if (forecastSubsetSize === populationSize) {
        subsetDescription = `All turbines from analysis (100%)`;
      } else if (forecastSubsetSize < populationSize) {
        subsetDescription = `Filtered subset (${percentage}% of ${populationSize} turbines)`;
      }
    } else if (populationSizeFromResults > 0) {
      subsetDescription = 'Turbines included in forecast';
    }
    
    // Add planned failures info if present
    if (totalPlannedFailures > 0) {
      subsetDescription += ` (${totalPlannedFailures.toFixed(0)} planned)`;
    }
    
    // Add excluded turbines info if available
    if (simulationMetadata?.excludedTurbines && simulationMetadata.excludedTurbines > 0) {
      subsetDescription += `. ${simulationMetadata.excludedTurbines} turbines not yet commissioned at forecast start`;
    }
    
    return {
      totalExpectedFailures,
      totalPlannedFailures,
      totalLowerBound: hasCumulativeCI ? lastResult.cumulativeLowerBound : undefined,
      totalUpperBound: hasCumulativeCI ? lastResult.cumulativeUpperBound : undefined,
      averagePerPeriod,
      periodType,
      periodTypeLower,
      periodTypeAdjective,
      periodsPerYear,
      peakPeriodTotal,
      peakPeriod,
      peakLowerBound: hasPeriodCI ? peakPeriodResult.lowerBound : undefined,
      peakUpperBound: hasPeriodCI ? peakPeriodResult.upperBound : undefined,
      populationSize: populationSizeFromResults,
      subsetDescription,
      horizonYears: Math.ceil(results.length / periodsPerYear)
    };
  }, [results, populationSize, forecastSubsetSize, simulationMetadata?.excludedTurbines]);

  if (!results.length || !summaryStats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Expected Failures"
          value="—"
          description="Load analysis to calculate forecast"
          color="bright-blue"
          loading={false}
        />
        
        <StatsCard
          title="Average Rate"
          value="—"
          description="Select time period for forecast"
          color="green"
          loading={false}
        />
        
        <StatsCard
          title="Peak Period"
          value="—"
          description="Configure forecast to identify peak"
          color="purple"
          loading={false}
        />
        
        <StatsCard
          title="Forecast Population"
          value="—"
          description="No turbines selected"
          color="fleet-monitor-blue"
          loading={false}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <StatsCard
        title="Expected Failures"
        value={summaryStats.totalExpectedFailures.toFixed(1)}
        description={
          summaryStats.totalLowerBound !== undefined && summaryStats.totalUpperBound !== undefined
            ? `${Math.round(confidenceLevel * 100)}% CI: [${summaryStats.totalLowerBound.toFixed(1)}, ${summaryStats.totalUpperBound.toFixed(1)}]`
            : `Total forecast failures over ${summaryStats.horizonYears} year${summaryStats.horizonYears !== 1 ? 's' : ''}`
        }
        color="bright-blue"
        loading={false}
      />
      
      <StatsCard
        title={`Average ${summaryStats.periodTypeAdjective} Rate`}
        value={summaryStats.averagePerPeriod.toFixed(1)}
        description={`Expected failures per ${summaryStats.periodTypeLower}`}
        color="green"
        loading={false}
      />
      
      <StatsCard
        title={`Peak ${summaryStats.periodType}`}
        value={summaryStats.peakPeriodTotal.toFixed(1)}
        description={
          summaryStats.peakLowerBound !== undefined && summaryStats.peakUpperBound !== undefined
            ? `${summaryStats.peakPeriod} (${Math.round(confidenceLevel * 100)}% CI: [${summaryStats.peakLowerBound.toFixed(1)}, ${summaryStats.peakUpperBound.toFixed(1)}])`
            : `Highest failures in ${summaryStats.peakPeriod}`
        }
        color="purple"
        loading={false}
      />
      
      <StatsCard
        title="Forecast Population"
        value={summaryStats.populationSize.toString()}
        description={summaryStats.subsetDescription}
        color="fleet-monitor-blue"
        loading={false}
      />
    </div>
  );
};

export const ForecastChart: React.FC<ForecastChartProps> = ({
  results,
  isLoading: _isLoading,
  title,
  className = '',
  includeConfidenceIntervals = false,
  includePlannedFailures = false,
  granularityMonths = 12
}) => {
  const chartOption = useMemo(() => {
    if (!results.length) return null;

    const hasCumulativeCI = includeConfidenceIntervals && results.some(r => 
      r.cumulativeLowerBound !== undefined && r.cumulativeUpperBound !== undefined
    );
    
    const hasPeriodCI = includeConfidenceIntervals && results.some(r => 
      r.lowerBound !== undefined && r.upperBound !== undefined
    );
    
    const hasPlannedFailures = includePlannedFailures && results.some(r => 
      r.plannedFailures !== undefined && r.plannedFailures > 0
    );

    const resultGranularity = results[0]?.granularityMonths || granularityMonths;
    
    // Prepare data arrays
    const categories: string[] = [];
    const periodFailures: number[] = [];
    const plannedFailures: number[] = [];
    const cumulativeFailures: number[] = [];
    const cumulativeLowerBound: number[] = [];
    const cumulativeUpperBound: number[] = [];
    const periodLowerBound: number[] = [];
    const periodUpperBound: number[] = [];
    
    let cumulativeTotal = 0;
    let previousYear: number | undefined;
    
    results.forEach((result, index) => {
      const periodDate = new Date(result.periodStartDate);
      
      // Create short label based on the actual period start date
      let label: string;
      const currentYear = periodDate.getFullYear();
      
      if (resultGranularity === 12) {
        // Yearly
        label = currentYear.toString();
      } else if (resultGranularity === 3) {
        // Quarterly
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        label = `${months[periodDate.getMonth()]} ${currentYear}`;
      } else {
        // Monthly
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        label = months[periodDate.getMonth()];
        if (previousYear !== undefined && previousYear !== currentYear) {
          label += ` '${currentYear.toString().slice(-2)}`;
        } else if (index === 0) {
          label += ` ${currentYear}`;
        }
      }
      
      categories.push(label);
      previousYear = currentYear;
      
      periodFailures.push(result.expectedFailures);
      plannedFailures.push(result.plannedFailures || 0);
      
      // Calculate cumulative WITHOUT planned failures (only predicted)
      // Planned failures don't affect the Weibull cumulative prediction
      cumulativeTotal += result.expectedFailures;
      cumulativeFailures.push(cumulativeTotal);
      
      if (hasCumulativeCI) {
        cumulativeLowerBound.push(result.cumulativeLowerBound!);
        cumulativeUpperBound.push(result.cumulativeUpperBound!);
      }
      if (hasPeriodCI) {
        periodLowerBound.push(result.lowerBound || result.expectedFailures);
        periodUpperBound.push(result.upperBound || result.expectedFailures);
      }
    });

    const isYearlyData = resultGranularity === 12;
    const isQuarterlyData = resultGranularity === 3;
    
    // Simpler rotation for shorter labels
    const labelRotation = isYearlyData ? 0 : (isQuarterlyData ? 0 : 45);

    const option: EChartsOption = {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold',
          color: '#1f2937'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: function(params: unknown) {
          const paramsArray = params as Array<{ 
            axisValue: string; 
            seriesName: string; 
            value: number | number[]; 
            color: string;
            componentSubType?: string;
          }>;
          
          const periodData = paramsArray.find(p => p.seriesName === 'Predicted Failures');
          const plannedData = paramsArray.find(p => p.seriesName === 'Planned Maintenance');
          const errorBarData = paramsArray.find(p => p.seriesName === 'Confidence Interval (95%)');
          const cumulativeData = paramsArray.find(p => p.seriesName === 'Cumulative Predicted');
          
          // Get the full period label from the result
          const resultIndex = categories.indexOf(paramsArray[0].axisValue);
          const fullPeriodLabel = results[resultIndex]?.timePeriod || paramsArray[0].axisValue;
          
          let tooltipText = `<div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${fullPeriodLabel}</div>`;
          
          // Period failures with confidence interval
          if (periodData) {
            const value = typeof periodData.value === 'number' ? periodData.value.toFixed(2) : periodData.value;
            tooltipText += `
              <div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background-color: ${periodData.color}; border-radius: 2px; margin-right: 8px;"></span>
                <strong>Predicted Failures:</strong> <span style="margin-left: 4px;">${value}</span>
              </div>
            `;
            
            if (errorBarData && Array.isArray(errorBarData.value) && errorBarData.value.length >= 4) {
              const lower = errorBarData.value[2].toFixed(2);
              const upper = errorBarData.value[3].toFixed(2);
              tooltipText += `
                <div style="margin: 4px 0 4px 20px; color: #6b7280; font-size: 12px;">
                  CI: [${lower}, ${upper}]
                </div>
              `;
            }
          }
          
          // Planned failures
          if (plannedData && plannedData.value > 0) {
            const value = typeof plannedData.value === 'number' ? plannedData.value.toFixed(2) : plannedData.value;
            tooltipText += `
              <div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background-color: ${plannedData.color}; border-radius: 2px; margin-right: 8px;"></span>
                <strong>Planned Maintenance:</strong> <span style="margin-left: 4px;">${value}</span>
              </div>
            `;
          }
          
          // Cumulative failures with confidence interval
          if (cumulativeData) {
            const value = typeof cumulativeData.value === 'number' ? cumulativeData.value.toFixed(2) : cumulativeData.value;
            tooltipText += `
              <div style="margin: 8px 0 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background-color: ${cumulativeData.color}; border-radius: 50%; margin-right: 8px;"></span>
                <strong>Cumulative Predicted:</strong> <span style="margin-left: 4px;">${value}</span>
              </div>
            `;
            
            const currentIndex = categories.indexOf(paramsArray[0].axisValue);
            if (hasCumulativeCI && currentIndex >= 0) {
              const lower = cumulativeLowerBound[currentIndex]?.toFixed(2);
              const upper = cumulativeUpperBound[currentIndex]?.toFixed(2);
              if (lower && upper) {
                tooltipText += `
                  <div style="margin: 4px 0 4px 20px; color: #6b7280; font-size: 12px;">
                    CI: [${lower}, ${upper}]
                  </div>
                `;
              }
            }
          }
          
          return tooltipText;
        }
      },
      legend: {
        data: [
          'Predicted Failures',
          ...(hasPlannedFailures ? ['Planned Maintenance'] : []),
          'Cumulative Predicted',
          ...(hasCumulativeCI ? [{
            name: 'Cumulative CI Band',
            icon: 'rect',
            itemStyle: {
              color: 'rgba(16, 185, 129, 0.2)',
              borderColor: 'rgba(16, 185, 129, 0.4)',
              borderWidth: 1
            }
          }] : [])
        ],
        top: 30,
        itemGap: 20,
        textStyle: {
          color: '#374151'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '15%',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: categories,
          axisPointer: {
            type: 'shadow'
          },
          axisLabel: {
            rotate: labelRotation,
            color: '#6b7280',
            fontSize: 11
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Period Failures',
          position: 'left',
          alignTicks: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#3b82f6'
            }
          },
          axisLabel: {
            formatter: '{value}',
            color: '#6b7280'
          },
          nameTextStyle: {
            color: '#3b82f6',
            padding: [0, 0, 10, 0]
          }
        },
        {
          type: 'value',
          name: 'Cumulative Failures',
          position: 'right',
          alignTicks: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#10b981'
            }
          },
          axisLabel: {
            formatter: '{value}',
            color: '#6b7280'
          },
          nameTextStyle: {
            color: '#10b981',
            padding: [0, 0, 10, 0]
          }
        }
      ],
      series: [
        // Predicted
        {
          name: 'Predicted Failures',
          type: 'bar',
          yAxisIndex: 0,
          data: periodFailures,
          barWidth: hasPlannedFailures ? '35%' : (isYearlyData ? '40%' : (isQuarterlyData ? '60%' : '70%')),
          itemStyle: {
            color: '#3b82f6',
            opacity: 0.8
          },
          emphasis: {
            itemStyle: {
              opacity: 1
            }
          }
        },
        // Planned
        ...(hasPlannedFailures ? [{
          name: 'Planned Maintenance',
          type: 'bar' as const,
          yAxisIndex: 0,
          data: plannedFailures,
          barWidth: '35%',
          itemStyle: {
            color: '#f59e0b',
            opacity: 0.8
          },
          emphasis: {
            itemStyle: {
              opacity: 1
            }
          }
        }] : []),
        ...(hasCumulativeCI ? [{
          name: 'Cumulative CI Band',
          type: 'line' as const,
          yAxisIndex: 1,
          data: cumulativeLowerBound,
          lineStyle: {
            opacity: 0
          },
          stack: 'confidence-band',
          symbol: 'none',
          silent: true,
          tooltip: {
            show: false
          },
          legendHoverLink: false
        },
        {
          name: 'Cumulative CI Band',
          type: 'line' as const,
          yAxisIndex: 1,
          data: cumulativeUpperBound.map((upper, i) => upper - cumulativeLowerBound[i]),
          lineStyle: {
            opacity: 0
          },
          stack: 'confidence-band',
          areaStyle: {
            color: 'rgba(16, 185, 129, 0.2)'
          },
          symbol: 'none',
          silent: true,
          tooltip: {
            show: false
          },
          legendHoverLink: false
        }] : []),
        {
          name: 'Cumulative Predicted',
          type: 'line',
          yAxisIndex: 1,
          data: cumulativeFailures,
          lineStyle: {
            color: '#10b981',
            width: 3
          },
          itemStyle: {
            color: '#10b981'
          },
          symbol: 'circle',
          symbolSize: 6,
          smooth: false,
          z: 101
        }
      ]
    };

    return option;
  }, [results, title, includeConfidenceIntervals, includePlannedFailures, granularityMonths]);


  if (!results.length) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No forecast data</h4>
            <p className="text-gray-600">Load an analysis and calculate forecast to see results</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="h-[420px]">
        {chartOption && (
          <ReactECharts
            option={chartOption}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        )}
      </div>
    </div>
  );
};
